import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, inr, fmtDate, today } from "@/lib/api";
import { StatCard, Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { WhatsAppPdfShare, downloadCsv } from "@/components/market";

export default function VendorStatement() {
  const { id } = useParams();
  const [f, setF] = useState({ date_from: "", date_to: "" });
  const [st, setSt] = useState(null);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get(`/market/vendors/${id}/statement`, { params }).then((r) => setSt(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id, f]);
  if (!st) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const csv = () => downloadCsv(`Statement_${st.vendor.name}_${today()}.csv`, ["Date", "Particulars", "Credit (+)", "Debit (-)", "Balance"],
    [["", "Opening Balance", "", "", st.opening], ...st.rows.map((r) => [r.date, r.particulars, r.credit || "", r.debit || "", r.balance]), ["", "Closing Balance", st.credit, st.debit, st.closing]]);
  return (
    <div className="space-y-5" data-testid="vendor-statement-page">
      <div className="flex flex-wrap items-start gap-3 fade-up">
        <Link to="/market/vendors" className="rounded-lg border p-2 hover:bg-secondary" data-testid="back-to-vendors"><ArrowLeft className="h-4 w-4" /></Link>
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold tracking-tight" data-testid="page-title">{st.vendor.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Vendor Ledger Statement · {st.vendor.category} · PAN {st.vendor.pan_number} · {st.trips} trips</p>
        </div>
        <Button variant="outline" data-testid="statement-csv-btn" onClick={csv}><Download className="mr-1 h-4 w-4" />Excel / CSV</Button>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="From"><Input type="date" data-testid="statement-from" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></Field>
            <Field label="To"><Input type="date" data-testid="statement-to" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Opening" value={inr(st.opening, 2)} testid="statement-opening" />
            <StatCard label="Credit (+)" value={inr(st.credit, 2)} accent="text-emerald-600" testid="statement-credit" />
            <StatCard label="Debit (−)" value={inr(st.debit, 2)} accent="text-rose-600" testid="statement-debit" />
            <StatCard label="Balance Payable" value={inr(st.closing, 2)} testid="statement-closing" />
          </div>
          <TableWrap>
            <thead><tr>{["Date", "Particulars", "Credit (+)", "Debit (−)", "Balance"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              <tr className="bg-secondary/40"><td className={td} /><td className={`${td} font-semibold`}>Opening Balance</td><td className={td} /><td className={td} /><td className={`${td} amount font-semibold`}>{inr(st.opening, 2)}</td></tr>
              {st.rows.map((r, i) => (
                <tr key={i} className={tr} data-testid={`statement-row-${i}`}>
                  <td className={`${td} whitespace-nowrap`}>{fmtDate(r.date)}</td>
                  <td className={td}>{r.particulars}</td>
                  <td className={`${td} amount text-emerald-600`}>{r.credit ? inr(r.credit, 2) : ""}</td>
                  <td className={`${td} amount text-rose-600`}>{r.debit ? inr(r.debit, 2) : ""}</td>
                  <td className={`${td} amount`}>{inr(r.balance, 2)}</td>
                </tr>
              ))}
              <tr className="border-t bg-secondary/60 font-semibold"><td className={td} /><td className={td}>Closing Balance</td><td className={`${td} amount`}>{inr(st.credit, 2)}</td><td className={`${td} amount`}>{inr(st.debit, 2)}</td><td className={`${td} amount`} data-testid="statement-closing-row">{inr(st.closing, 2)}</td></tr>
            </tbody>
          </TableWrap>
          {!st.rows.length && <Empty testid="statement-empty" text="No entries in this period" />}
        </div>
        <div className="card-base h-fit p-5" data-testid="statement-share-card">
          <h3 className="mb-3 font-semibold">Send Statement to Vendor</h3>
          <WhatsAppPdfShare info={{ ...st, phone: st.vendor.phone }} testid="statement-share" />
        </div>
      </div>
    </div>
  );
}
